import { Controller } from '@hotwired/stimulus';
// Extension `.js` explicite : esbuild s'en passe, `node --test` non.
import { isPrerendering, isTurboPreview, withoutSearchParam } from '../utils/auto_open_guard.js';

/**
 * Auto-opens a modal on connect and removes itself from the DOM.
 *
 * Used by Turbo Stream responses to trigger modal opening after DOM updates.
 * Example: after rejecting an invoice, open the next invoice's preview modal.
 * The element MUST come after the modal in the DOM (Stimulus connects in order).
 *
 * Values:
 *   modalId     — id of the modal to open (`window.openModal`).
 *   forgetParam — optional name of a URL search parameter that asked for this opening
 *                 (Suivi financier, décision 08 : `facture` / `devis`, G-5). Set:
 *                 - once the modal is open, the parameter is removed from the URL by
 *                   `history.replaceState` (Turbo's state kept), so a reload, a Back or a
 *                   morph refresh never reopens it (SS12);
 *                 - nothing opens on a Turbo preview (`html[data-turbo-preview]`, cached
 *                   snapshot painted before the response): the real render that follows
 *                   brings its own opener;
 *                 - on a browser prerender (`document.prerendering`), opening waits for
 *                   the page's activation (`prerenderingchange`, removed on disconnect).
 *                 Empty (default): behaviour unchanged for every other caller.
 *
 * Usage (in a Turbo Stream template):
 *   <div data-controller="auto-open-modal"
 *        data-auto-open-modal-modal-id-value="invoice-preview-42">
 *   </div>
 *
 * Usage (arrival from Pilotage, after the invoice windows):
 *   <div data-controller="auto-open-modal"
 *        data-auto-open-modal-modal-id-value="invoice-preview-42"
 *        data-auto-open-modal-forget-param-value="facture"></div>
 */
export default class extends Controller {
  static values = {
    modalId: String,
    forgetParam: { type: String, default: '' },
  };

  connect() {
    if (this.forgetParamValue) {
      if (isTurboPreview(document)) return;
      if (isPrerendering(document)) {
        this.onActivated = () => {
          this.onActivated = null;
          this.#open();
        };
        document.addEventListener('prerenderingchange', this.onActivated, { once: true });
        return;
      }
    }
    this.#open();
  }

  disconnect() {
    if (this.onActivated) document.removeEventListener('prerenderingchange', this.onActivated);
    this.onActivated = null;
  }

  #open() {
    const modal = this.modalIdValue ? document.getElementById(this.modalIdValue) : null;

    if (this.modalIdValue) {
      window.openModal(this.modalIdValue);
    }

    // A 422 re-renders the whole modal with field-level error semantics. Let the
    // generic modal establish its initial focus first, then move to the first
    // invalid form control so its linked French message is announced.
    requestAnimationFrame(() => {
      modal
        ?.querySelector(
          'input[aria-invalid="true"], select[aria-invalid="true"], textarea[aria-invalid="true"]'
        )
        ?.focus({ preventScroll: true });
    });

    if (this.forgetParamValue) this.#forgetParam();

    // Clean up: remove the trigger element from the DOM
    this.element.remove();
  }

  // One shot: the intent carried by the URL is consumed by this opening.
  #forgetParam() {
    const next = withoutSearchParam(window.location.href, this.forgetParamValue);
    if (next) window.history.replaceState(window.history.state, '', next);
  }
}
